/**
 * A rule box's rules and price as a merchant types them, shared by the embedded admin's builder
 * (apps/shopify) and the dashboard's rule editor, so both read a form the same way. Shopify shops
 * are USD, so prices here are dollars, never the tenant's storefront currency.
 */
import {
  RULE_BOX_DEFAULT_MARGIN_BPS,
  RULE_BOX_LOWEST_MIN_RETURN_BPS,
  RULE_BOX_MAX_ITEMS,
  RULE_BOX_MAX_MARGIN_BPS,
  RULE_BOX_MIN_ITEMS,
  RULE_BOX_MIN_MARGIN_BPS,
  RULE_BOX_MIN_RETURN_BPS,
  RULE_BOX_NEW_MIN_RETURN_BPS,
  type RuleBoxConfig,
  type RuleBoxPriceMode,
  type RuleBoxRules,
} from "./shopifyRuleBox.js";

/** Cents as dollars, for a USD shop. */
export function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * A dollars field ("12.50", "$1,200") in whole cents, or null when it is not a price above zero
 * with at most two decimal places.
 */
export function parseDollars(value: string): number | null {
  const cleaned = value.trim().replace(/^\$/, "").replaceAll(",", "");
  if (!/^\d+(\.\d{0,2})?$|^\.\d{1,2}$/.test(cleaned)) return null;
  const cents = Math.round(Number(cleaned) * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

/** Cents as a dollars field's text: "50" for whole dollars, "49.99" otherwise. */
export function dollarsField(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

/**
 * The builder's rules and price as the merchant types them: lists of chosen tags, vendors and
 * product types, and text for every number (dollars and a margin percentage), so a half-typed
 * field stays as typed. `parseRuleBoxForm` turns it into the config the server takes.
 */
export interface RuleBoxForm {
  includeTags: string[];
  excludeTags: string[];
  vendors: string[];
  productTypes: string[];
  /** Dollars; "" = no lower bound. */
  minPrice: string;
  /** Dollars; "" = no upper bound. */
  maxPrice: string;
  /** Percent of the price the merchant keeps on average, e.g. "20". */
  marginPercent: string;
  priceMode: RuleBoxPriceMode;
  /** Dollars; read only with the `fixed` price mode. */
  fixedPrice: string;
  /** A whole number; "" = no limit of the merchant's own. */
  minItems: string;
  /** A whole number; "" = no limit of the merchant's own. */
  maxItems: string;
  /**
   * Percent of the box price every item is worth at least, e.g. "50"; "" = the platform's
   * `RULE_BOX_MIN_RETURN_BPS`, what a box saved before this setting holds.
   */
  minReturnPercent: string;
}

/** The typed fields a parse can refuse, each with its own message. */
export type RuleBoxFormField =
  | "minPrice"
  | "maxPrice"
  | "marginPercent"
  | "fixedPrice"
  | "minItems"
  | "maxItems"
  | "minReturnPercent";

export type ParsedRuleBoxForm =
  | { ok: true; config: RuleBoxConfig }
  | { ok: false; errors: Partial<Record<RuleBoxFormField, string>> };

/** The lowest and highest margin a merchant may pick, as percentages. */
export const MIN_MARGIN_PERCENT = RULE_BOX_MIN_MARGIN_BPS / 100;
export const MAX_MARGIN_PERCENT = RULE_BOX_MAX_MARGIN_BPS / 100;

/** The lowest and highest minimum return a merchant may pick, as percentages. */
export const LOWEST_MIN_RETURN_PERCENT = RULE_BOX_LOWEST_MIN_RETURN_BPS / 100;
export const HIGHEST_MIN_RETURN_PERCENT = RULE_BOX_MIN_RETURN_BPS / 100;

/** The price modes in the merchant's words. */
export const PRICE_MODE_LABEL: Record<RuleBoxPriceMode, string> = {
  auto: "Estimated",
  fixed: "Fixed price",
};

/**
 * A new box's form: no rules, the default margin, an estimated price, and the minimum return that
 * lets most catalogs fill tiered odds (`RULE_BOX_NEW_MIN_RETURN_BPS`).
 */
export function emptyRuleBoxForm(): RuleBoxForm {
  return {
    includeTags: [],
    excludeTags: [],
    vendors: [],
    productTypes: [],
    minPrice: "",
    maxPrice: "",
    marginPercent: String(RULE_BOX_DEFAULT_MARGIN_BPS / 100),
    priceMode: "auto",
    fixedPrice: "",
    minItems: "",
    maxItems: "",
    minReturnPercent: String(RULE_BOX_NEW_MIN_RETURN_BPS / 100),
  };
}

/** A saved config as the form shows it, for an edit. */
export function ruleBoxFormFromConfig(config: RuleBoxConfig): RuleBoxForm {
  const { rules } = config;
  return {
    includeTags: [...rules.includeTags],
    excludeTags: [...rules.excludeTags],
    vendors: [...rules.vendors],
    productTypes: [...rules.productTypes],
    minPrice: rules.minPriceCents == null ? "" : dollarsField(rules.minPriceCents),
    maxPrice: rules.maxPriceCents == null ? "" : dollarsField(rules.maxPriceCents),
    marginPercent: String(config.marginBps / 100),
    priceMode: config.priceMode,
    fixedPrice: config.fixedPriceCents == null ? "" : dollarsField(config.fixedPriceCents),
    minItems: config.minItems == null ? "" : String(config.minItems),
    maxItems: config.maxItems == null ? "" : String(config.maxItems),
    minReturnPercent: config.minReturnBps == null ? "" : String(config.minReturnBps / 100),
  };
}

/** A percentage field ("20", "12.5", "20%") in basis points, or null when it is not one. */
function parsePercentBps(value: string): number | null {
  const trimmed = value.trim().replace(/%$/, "");
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  return Math.round(Number(trimmed) * 100);
}

/** A margin field ("20", "12.5") in basis points within the allowed range, or null. */
export function parseMarginPercent(value: string): number | null {
  const bps = parsePercentBps(value);
  return bps !== null && bps >= RULE_BOX_MIN_MARGIN_BPS && bps <= RULE_BOX_MAX_MARGIN_BPS
    ? bps
    : null;
}

/** A minimum return field ("50") in basis points within the allowed range, or null. */
export function parseMinReturnPercent(value: string): number | null {
  const bps = parsePercentBps(value);
  return bps !== null && bps >= RULE_BOX_LOWEST_MIN_RETURN_BPS && bps <= RULE_BOX_MIN_RETURN_BPS
    ? bps
    : null;
}

/**
 * An item count field ("25") as a whole number from `RULE_BOX_MIN_ITEMS` to `RULE_BOX_MAX_ITEMS`,
 * or null.
 */
export function parseItemCount(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const count = Number(trimmed);
  return count >= RULE_BOX_MIN_ITEMS && count <= RULE_BOX_MAX_ITEMS ? count : null;
}

/**
 * The form as the config `boxes.preview`, `create` and `update` take, or the message for every
 * field it cannot read. An empty price bound or item limit is no bound.
 */
export function parseRuleBoxForm(form: RuleBoxForm): ParsedRuleBoxForm {
  const errors: Partial<Record<RuleBoxFormField, string>> = {};
  const minPriceCents = form.minPrice.trim() === "" ? null : parseDollars(form.minPrice);
  const maxPriceCents = form.maxPrice.trim() === "" ? null : parseDollars(form.maxPrice);
  if (form.minPrice.trim() !== "" && minPriceCents === null) {
    errors.minPrice = "Enter a price, like 50 or 49.99.";
  }
  if (form.maxPrice.trim() !== "" && maxPriceCents === null) {
    errors.maxPrice = "Enter a price, like 800 or 799.99.";
  }
  if (minPriceCents !== null && maxPriceCents !== null && minPriceCents > maxPriceCents) {
    errors.maxPrice = "The highest price must be at least the lowest.";
  }
  const marginBps = parseMarginPercent(form.marginPercent);
  if (marginBps === null) {
    errors.marginPercent = `Enter a margin from ${MIN_MARGIN_PERCENT}% to ${MAX_MARGIN_PERCENT}%.`;
  }
  const fixedPriceCents = form.priceMode === "fixed" ? parseDollars(form.fixedPrice) : null;
  if (form.priceMode === "fixed" && (fixedPriceCents === null || fixedPriceCents < 100)) {
    errors.fixedPrice = "Enter a price of at least $1, like 75 or 74.99.";
  }
  const minItems = form.minItems.trim() === "" ? null : parseItemCount(form.minItems);
  const maxItems = form.maxItems.trim() === "" ? null : parseItemCount(form.maxItems);
  const countRange = `a whole number from ${RULE_BOX_MIN_ITEMS} to ${RULE_BOX_MAX_ITEMS}`;
  if (form.minItems.trim() !== "" && minItems === null) {
    errors.minItems = `Enter ${countRange}, like 25.`;
  }
  if (form.maxItems.trim() !== "" && maxItems === null) {
    errors.maxItems = `Enter ${countRange}, like 36.`;
  }
  if (minItems !== null && maxItems !== null && minItems > maxItems) {
    errors.maxItems = "The most items must be at least the fewest.";
  }
  const minReturnBps =
    form.minReturnPercent.trim() === "" ? null : parseMinReturnPercent(form.minReturnPercent);
  if (form.minReturnPercent.trim() !== "" && minReturnBps === null) {
    errors.minReturnPercent = `Enter a percentage from ${LOWEST_MIN_RETURN_PERCENT}% to ${HIGHEST_MIN_RETURN_PERCENT}%.`;
  }
  if (Object.keys(errors).length > 0 || marginBps === null) return { ok: false, errors };
  return {
    ok: true,
    config: {
      rules: {
        includeTags: form.includeTags,
        excludeTags: form.excludeTags,
        vendors: form.vendors,
        productTypes: form.productTypes,
        minPriceCents,
        maxPriceCents,
      },
      marginBps,
      priceMode: form.priceMode,
      fixedPriceCents,
      minItems,
      maxItems,
      minReturnBps,
    },
  };
}

/** `list` with `value` added once, ignoring case and surrounding spaces. */
export function addChoice(list: string[], value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return list;
  const lower = trimmed.toLowerCase();
  return list.some((v) => v.toLowerCase() === lower) ? list : [...list, trimmed];
}

/**
 * The options not yet chosen that contain `query`, ignoring case: those starting with it first,
 * then the rest, each group alphabetical. An empty query lists every unchosen option.
 */
export function rankChoices(options: string[], chosen: string[], query: string): string[] {
  const taken = new Set(chosen.map((v) => v.toLowerCase()));
  const q = query.trim().toLowerCase();
  const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });
  const open = options.filter((o) => !taken.has(o.toLowerCase()) && o.toLowerCase().includes(q));
  const starts = open.filter((o) => o.toLowerCase().startsWith(q)).sort(byName);
  const contains = open.filter((o) => !o.toLowerCase().startsWith(q)).sort(byName);
  return [...starts, ...contains];
}

/** A choice list as its first value and how many more: "rolex +2". */
function firstAndCount(values: string[]): string {
  const [first, ...rest] = values;
  return rest.length ? `${first} +${rest.length}` : (first ?? "");
}

/**
 * A box's rules short enough for one line of a table row, each list cut to its first value and a
 * count: "Tagged rolex +2 · By Gamba · $50.00–$800.00". `describeRules` writes every value.
 */
export function summarizeRules(rules: RuleBoxRules): string {
  return describeRules({
    ...rules,
    includeTags: rules.includeTags.length ? [firstAndCount(rules.includeTags)] : [],
    excludeTags: rules.excludeTags.length ? [firstAndCount(rules.excludeTags)] : [],
    vendors: rules.vendors.length ? [firstAndCount(rules.vendors)] : [],
    productTypes: rules.productTypes.length ? [firstAndCount(rules.productTypes)] : [],
  });
}

/** A box's rules in one line: "Tagged rolex · Not tagged used · $50.00–$800.00". */
export function describeRules(rules: RuleBoxRules): string {
  const parts: string[] = [];
  if (rules.includeTags.length) parts.push(`Tagged ${rules.includeTags.join(" or ")}`);
  if (rules.excludeTags.length) parts.push(`Not tagged ${rules.excludeTags.join(" or ")}`);
  if (rules.vendors.length) parts.push(`By ${rules.vendors.join(" or ")}`);
  if (rules.productTypes.length) parts.push(`Type ${rules.productTypes.join(" or ")}`);
  const { minPriceCents: min, maxPriceCents: max } = rules;
  if (min != null && max != null) parts.push(`${dollars(min)}–${dollars(max)}`);
  else if (min != null) parts.push(`${dollars(min)} and up`);
  else if (max != null) parts.push(`Up to ${dollars(max)}`);
  return parts.length ? parts.join(" · ") : "All your products";
}
